import { DOMOutputSpec, DOMOutputSpecArray, Node, NodeSpec } from 'prosemirror-model';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NodeName } from '../../schema/nodes';
import { MentionElement, MentionType } from './types';

type MentionTypeElementMap = Record<MentionType, Record<number, MentionElement>>;

export class MentionNode implements NodeSpec {
  public static readonly nodeName = NodeName.Mention;
  public attrs = {
    'data-id': {},
    'data-type': {},
  };
  public inline = true;
  public atom = true;
  public content = 'inline+';
  public group = 'inline';
  public selectable = false;
  public draggable = false;
  public parseDOM = [
    {
      tag: 'rt-mention',
      getAttrs: (node: HTMLElement) => ({
        'data-id': parseInt(node.dataset['id']!, 10),
        'data-type': node.dataset['type'],
      }),
    },
  ];

  private destroy$ = new Subject<void>();
  private idDataMap: MentionTypeElementMap;

  constructor(private dataSource?: Observable<MentionElement[]>) {
    if (this.dataSource) {
      this.dataSource.pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.idDataMap = data.reduce((acc, item) => {
          if (!acc[item.type]) acc[item.type] = {};
          acc[item.type][item.id] = item;
          return acc;
        }, <MentionTypeElementMap>{});
      });
    }
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toDOM = (node: Node): DOMOutputSpec => {
    const id = parseInt(node.attrs['data-id'], 10);
    const type: MentionType = node.attrs['data-type'];
    const data = this.getNodeData(id, type);

    return [
      'rt-mention',
      {
        'data-id': `${id}`,
        'data-type': type,
      },
      `@${data?.name || node.textContent}`,
    ];
  };

  private getNodeData(id: number, type: MentionType): MentionElement | null {
    if (!id || !type || !this.dataSource || !this.idDataMap) return null;

    return this.idDataMap[type][id];
  }
}

export class LegacyMentionNode implements NodeSpec {
  public static readonly nodeName = NodeName.LegacyMention;
  public inline = true;
  public atom = true;
  public content = 'inline+';
  public group = 'inline';
  public selectable = true;
  public draggable = false;
  public parseDOM = [
    {
      tag: 'span[class="mention"]',
    },
  ];

  public toDOM(node: Node): DOMOutputSpecArray {
    return ['span', { class: 'mention' }, node.textContent];
  }
}
